import { useState, FormEvent } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import PasswordInput from '../components/Auth/PasswordInput';

export default function LoginPage() {
  const { login, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/prequalification';

  const [taxId, setTaxId]       = useState('');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const successMessage = (location.state as { message?: string } | null)?.message;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.auth.login({ taxId, email, password });
      const u = res.user;
      const roles = u.roles ?? [u.role ?? 'company_user'];
      login({ ...u, roles, prequalificationStatus: u.prequalificationStatus ?? 'none', isRenewal: u.isRenewal ?? false });
      await refresh();

      // Redirect based on role — admins, reviewers, editors go to their dashboards, others to preq
      const isAdmin    = roles.some((r) => ['tenant_admin', 'super_admin'].includes(r));
      const isReviewer = roles.some((r) => r === 'reviewer');
      const isEditor   = roles.some((r) => r === 'editor');
      const defaultDest = isAdmin ? '/admin/applications' : isReviewer ? '/reviewer/applications' : isEditor ? '/editor/publication' : '/prequalification';
      const isDefaultFrom = from === '/prequalification' || from === '/login';
      navigate(isDefaultFrom ? defaultDest : from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ismeretlen hiba.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--background)', display: 'flex', flexDirection: 'column' }}>

      {/* ── Hero header ── */}
      <div style={{
        background: 'linear-gradient(135deg, var(--primary) 0%, #064944 58%, var(--secondary) 150%)',
        padding: '32px 40px', display: 'flex', alignItems: 'center', gap: 18,
      }}>
        <img src="/logo_gymsmkik.svg" style={{ height: 40, width: 'auto' }} alt="GYMSMKIK" />
        <div style={{ borderLeft: '1px solid rgba(255,255,255,.3)', paddingLeft: 18 }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#fff', lineHeight: 1.2 }}>
            Védjegy Digitalizációs Platform
          </div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,.72)', marginTop: 2 }}>
            GYMSM KIK, Védjegy pályázati portál
          </div>
        </div>
      </div>

      {/* ── Card ── */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 20px' }}>
        <div style={{
          background: '#fff', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)', padding: '44px 48px',
          boxShadow: 'var(--shadow-md)', width: '100%', maxWidth: 440,
        }}>
          <div style={{ marginBottom: 32 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--secondary-foreground)', marginBottom: 8 }}>
              BEJELENTKEZÉS
            </div>
            <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: 'var(--primary)' }}>
              Üdvözöljük!
            </h1>
            <p style={{ margin: '8px 0 0', fontSize: 14, color: 'var(--muted-foreground)' }}>
              Jelentkezzen be a pályázati rendszerbe.
            </p>
          </div>

          {successMessage && (
            <div className="alert alert-success" style={{ marginBottom: 20 }}>
              <span>✓</span>
              <div><p>{successMessage}</p></div>
            </div>
          )}

          {error && (
            <div className="alert alert-destructive" style={{ marginBottom: 20 }}>
              <span>⚠</span>
              <div><strong>Hiba</strong><p>{error}</p></div>
            </div>
          )}

          <form onSubmit={(e) => { void handleSubmit(e); }}>
            <div className="field">
              <label htmlFor="taxId">
                Adószám{' '}
                <span style={{ fontWeight: 400, color: 'var(--muted-foreground)', fontSize: 12 }}>
                  (csak az első 8 számjegy; adminok számára nem kötelező)
                </span>
              </label>
              <input
                id="taxId" type="text" autoComplete="off"
                value={taxId} onChange={(e) => setTaxId(e.target.value)}
                placeholder="12345678"
              />
            </div>

            <div className="field">
              <label htmlFor="email">E-mail cím <span className="req">*</span></label>
              <input
                id="email" type="email" autoComplete="email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="pelda@ceg.hu" required
              />
            </div>

            <div className="field" style={{ marginBottom: 8 }}>
              <label htmlFor="password">Jelszó <span className="req">*</span></label>
              <PasswordInput
                id="password" autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" required
              />
            </div>

            <div style={{ textAlign: 'right', marginBottom: 24 }}>
              <Link to="/forgot-password" style={{ fontSize: 13, color: 'var(--secondary-foreground)', textDecoration: 'none' }}>
                Elfelejtett jelszó?
              </Link>
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              style={{ width: '100%', marginBottom: 16 }}
              disabled={loading}
            >
              {loading ? 'Bejelentkezés…' : 'Bejelentkezés →'}
            </button>
          </form>

          <div style={{ textAlign: 'center', fontSize: 14, color: 'var(--muted-foreground)' }}>
            Még nincs fiókja?{' '}
            <Link to="/register" style={{ color: 'var(--primary)', fontWeight: 700, textDecoration: 'none' }}>
              Regisztráció
            </Link>
          </div>
        </div>
      </div>

      <div style={{ padding: '16px 40px', fontSize: 12, color: 'var(--muted-foreground)', textAlign: 'center' }}>
        © {new Date().getFullYear()} GYMSMKIK — Győr-Moson-Sopron Vármegyei Kereskedelmi és Iparkamara
      </div>
    </div>
  );
}
